<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg face-detection-sidebar"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? `${$t("actions.add")} ${$t("omnicloud_fr.face.title")}` :`${$t("actions.edit")} ${$t("omnicloud_fr.face.title")}` }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="() => handleSubmit(onSubmit)()"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            name="name"
            :rules="validationRules.name"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.face.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="active"
            :rules="validationRules.active"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.face.active')"
              label-for="active"
            >
              <b-form-checkbox
                id="active"
                v-model="itemData.active"
                :state="getValidationState(validationContext)"
                switch
                :value="true"
                :unchecked-value="false"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="comment"
            :rules="validationRules.comment"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.face.comment')"
              label-for="comment"
            >
              <b-form-textarea
                id="comment"
                v-model="itemData.comment"
                :state="getValidationState(validationContext)"
                trim
                rows="4"
                max-rows="6"
                :placeholder="$t('risk.add_comment')"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Watchlist -->
          <validation-provider
            #default="validationContext"
            name="watchlist"
            :rules="validationRules.watchlist"
          >
            <b-form-group
              :label="$tc('omnicloud_fr.face.watch_list', 2)"
              label-for="watchlist"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.lists"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="watchlistOptions"
                :reduce="(val) => val.value"
                label="label"
                input-id="watchlist"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div
            v-if="!isAdd && itemData.image_url"
            class="mt-2 d-flex flex-wrap">
            <div class="position-relative d-inline-block mr-2 mb-2">
              <img
                :src="itemData.image_url"
                alt="preview"
                class="img-thumbnail"
                style="width: 250px"
              />

              <b-button
                @click="itemData.image_url = ''"
                size="sm"
                variant="danger"
                class="position-absolute top-0 end-0 translate-middle rounded-circle p-0"
                style="width: 24px; height: 24px"
              >
                ×
              </b-button>
            </div>
          </div>

          <validation-provider
            v-else
            #default="validationContext"
            name="image_url"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.face.image')"
              label-for="image"
            >
              <div class="custom-file-upload">
                <b-form-file
                  id="image"
                  v-model="imageFile"
                  :state="getValidationState(validationContext)"
                  accept="image/*"
                  @change="handleImageUpload"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  browse-text="Browse"
                  class="tall-file-input"
                />
              </div>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Display detected faces -->
          <div v-if="detectedFaces.length > 0" class="mt-4">
            <div v-for="(face, index) in detectedFaces" :key="face.id" class="face-card mb-4 p-3 border rounded">
              <div class="d-flex align-items-start">
                <!-- Face image -->
                <div class="face-image mr-3 position-relative" style="width: 150px;">
                  <img :src="face.image" class="img-fluid rounded" />
                  <div v-if="face.isAdded" class="registered-label">
                    {{$t('omnicloud_fr.face.face_added_label')}}
                  </div>
                </div>

                <div class="flex-grow-1">
                  <validation-provider
                    #default="validationContext"
                    name="name"
                    rules="required"
                  >
                    <b-form-group
                      label="Nombre"
                      label-for="name"
                    >
                      <b-form-input
                        v-model="face.name"
                        id="name"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter name"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Name' }) }}
                      </b-form-invalid-feedback>
                      <small v-if="face.isAdded" class="text-success d-block mt-1">
                        <i class="feather icon-check mr-1"></i>{{$t('omnicloud_fr.face.already_face_added')}}
                      </small>
                    </b-form-group>
                  </validation-provider>

                  <validation-provider
                    #default="validationContext"
                    name="comment"
                    rules="required"
                  >
                    <b-form-group
                      label="Comentario"
                      label-for="comment"
                    >
                      <b-form-input
                        v-model="face.comment"
                        id="comment"
                        :state="getValidationState(validationContext)"
                        placeholder="Enter comment"
                      />
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Comment' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>

                  <validation-provider
                    #default="validationContext"
                    name="watchlist"
                    rules="required"
                  >
                    <b-form-group
                      :label="$tc('omnicloud_fr.face.watch_list', 2)"
                      label-for="watchlist"
                    >
                      <v-select
                        v-model="face.lists"
                        :options="watchlistOptions"
                        :reduce="(val) => val.value"
                        :state="getValidationState(validationContext)"
                        label="label"
                        input-id="watchlist"
                      />
                      <b-form-invalid-feedback :state="getValidationState(validationContext)">
                        {{ validationContext.errors[0] || $t('validation.required', { field: 'Watchlist' }) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>

                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="ml-2"
                  @click="removeFace(index)"
                >
                  <feather-icon icon="XIcon" size="16" />
                </b-button>
              </div>
            </div>
          </div>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              @click.prevent="validateAndSubmit"
              :disabled="isLoading"
            >
              <b-spinner
                small
                v-if="isLoading"
                class="mr-1"
              />
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
              :disabled="isLoading"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BFormFile,
  BSpinner,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, watch, computed } from "@vue/composition-api";
import { required, alphaNum, email, positive } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { extend } from 'vee-validate';

extend('requiredArray', {
  validate(value) {
    return Array.isArray(value) && value.length > 0;
  },
  message: 'El campo {_field_} es obligatorio'
});

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    BFormFile,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    watchlistOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    snapshotData: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const imageFile = ref(null);
    const isLoading = ref(false);
    const detectedFaces = ref([]);
    const { t } = useI18nUtils();
    const toast = useToast();
    const imageWithBbox = ref(null);
    const imageBboxClass = ref('');
    const translations = {
      "omnicloud_fr.face.no_faces_to_add": t("omnicloud_fr.face.no_faces_to_add"),
      "omnicloud_fr.face.success_adding": t("omnicloud_fr.face.success_adding"),
      "omnicloud_fr.face.success_updating": t("omnicloud_fr.face.success_updating"),
      "omnicloud_fr.face.error_saving": t("omnicloud_fr.face.error_saving"),
    };

    const blankData = {
      name: null,
      active: null,
      comment: null,
      image_url: null,
      api_server: null,
      lists: null,
      id: null,
    };

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };
    itemData.value.active = true;

    const removeFace = (index) => {
      detectedFaces.value.splice(index, 1);
    };

    const clearImageAndFaces = () => {
      detectedFaces.value = [];
      imageFile.value = null;
      imageWithBbox.value = null;
      const fileInput = document.getElementById('image');
      if (fileInput) {
        fileInput.value = '';
      }
      itemData.value.image_url = null;
    };

    const { refFormObserver, getValidationState, resetForm: baseResetForm } = formValidation(resetData);

    function resetForm() {
      baseResetForm();
      imageFile.value = null;
      if (props.isAdd) {
        detectedFaces.value = [];
        const fileInput = document.getElementById('image');
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }

    const loadData = () => {
      if (!props.isAdd && props.itemEdit) {
        itemData.value = {
          id: props.itemEdit.id || null,
          name: props.itemEdit.name || null,
          comment: props.itemEdit.comment || null,
          image_url: props.itemEdit.image_url || null,
          active: props.itemEdit.active !== undefined ? Boolean(props.itemEdit.active) : true,
          lists: props.itemEdit.lists || []
        };
      } else {
        resetForm();
      }
    };

    const submitForm = async (e) => {
      e.preventDefault();
      const isValid = await refFormObserver.value.validate();
      if (isValid) {
        await onSubmit();
      }
    };

    const onSubmit = async () => {
      isLoading.value = true;
      try {
        let response;
        const watchlistId = getListIds(itemData.value.lists);
        const formData = new FormData();
        formData.append('name', itemData.value.name);
        formData.append('comment', itemData.value.comment);
        formData.append('active', itemData.value.active ? 1 : 0);
        watchlistId.forEach(id => {
          formData.append('lists[]', id);
        });

        if (!props.isAdd) {
          if(imageFile.value) formData.append('photo', imageFile.value);
          response = await store.dispatch('omnicloud_face/edit',{id: props.itemEdit.id,params: formData});
        } else {
          formData.append('photo', imageFile.value);
          response = await store.dispatch('omnicloud_face/add', formData);

          if (response?.id) {
            resetForm();
            emit('update:is-sidebar-active', false);
          } else {
            errorMessageOnFail(response?.message)
            return;
          }
        }
        const successMessage = response?.message ?? props.isAdd ? translations['omnicloud_fr.face.success_adding'] : translations['omnicloud_fr.face.success_updating']
        toast({
          component: ToastificationContent,
          props: {
            title: successMessage,
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit('refetch-data');

        if (!props.isAdd) {
          resetForm();
          emit('update:is-sidebar-active', false);
        }
      } catch (error) {
        errorMessageOnFail(error.response?.data?.message)
      } finally {
        isLoading.value = false;
      }
    };

    const hasAddableFaces = computed(() => {
      return detectedFaces.value.some(face =>
        face.name && face.comment && face.lists
      );
    });

    const getListIds = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      const isNumbersOnly = arr.every(
        item => typeof item === 'number' && !Number.isNaN(item)
      );
      if(isNumbersOnly) return arr;
      return arr.map(item => item.value);
    };

    const errorMessageOnFail = (message) => {
      toast({
        component: ToastificationContent,
        props: {
          title: message|| translations['omnicloud_fr.face.error_saving'],
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          itemData.value.image_url = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    };

    const validateAndSubmit = async () => {
      try {
        if (refFormObserver.value) {
          const { valid, errors } = await refFormObserver.value.validate();
          if (valid || (itemData.value.name && itemData.value.comment && itemData.value.lists.length)) {

            await onSubmit();
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Please fill in all required fields',
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        }
      } catch (error) {
      }
    };

    const validationRules = computed(() => ({
      name: { required: true },
      comment: { required: true },
      watchlist: { required: true },
      active: { required: false } // Make active optional since it has a default value
    }));

    watch(() => props.itemEdit, (newVal) => {
      if (newVal && !props.isAdd) {
        loadData();
      }
    }, { immediate: true });

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      imageFile,
      handleImageUpload,
      isLoading,
      detectedFaces,
      removeFace,
      clearImageAndFaces,
      hasAddableFaces,
      submitForm,
      validateAndSubmit,
      validationRules,
      imageWithBbox,
      imageBboxClass,
    };
  },
};
</script>

<style>
.img-bbox-container {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 40vh;
  max-height: 60vh;
  margin: 0 auto;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.clear-image-btn {
  top: 10px;
  right: 10px;
  z-index: 10;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}


.carrousel-alarm-table .VueCarousel-navigation-prev {
  left: 40px;
}

.carrousel-alarm-table .VueCarousel-navigation-next {
  right: 40px;
}

/* Sidebar width customization */
.face-detection-sidebar {
  width: 600px !important;
  max-width: 80vw !important;
}

/* Dark mode styles */
.dark-layout .face-detection-sidebar {
  background-color: #283046 !important;
}

.dark-layout .face-detection-sidebar .content-sidebar-header {
  background-color: #161d31;
  border-bottom: 1px solid #3b4253;
}

.dark-layout .face-card {
  background: #283046 !important;
  border-color: #3b4253 !important;
}

.dark-layout .custom-file-upload .custom-file-label {
  background-color: #283046;
  border-color: #404656;
  color: #b4b7bd;
}

.dark-layout .custom-file-upload .custom-file-label::after {
  background-color: #161d31;
  border-color: #404656;
  color: #b4b7bd;
}

.dark-layout .form-control {
  background-color: #283046;
  /* border-color: #404656; */
  color: #b4b7bd;
}

.dark-layout .form-control:focus {
  /* background-color: var(--third-dark) !important; */
  border-color: #283046;
  color: #b4b7bd;
}

.dark-layout .vs__dropdown-toggle {
  background-color: var(--second-dark) !important;
  /* border-color: #404656; */
  color: #b4b7bd;
}

.dark-layout .vs__selected {
  color: #b4b7bd;
}

.dark-layout .vs__search::placeholder {
  color: #676d7d;
}

.dark-layout .vs__dropdown-menu {
  background-color: var(--main-dark) !important;
  /* border-color: #404656; */
}

.dark-layout .vs__dropdown-option {
  color: #b4b7bd;
}

.dark-layout .vs__dropdown-option--highlight {
  color: #fff;
}

.custom-file-upload {
  position: relative;
  margin-bottom: 1rem;
}

.custom-file-upload .custom-file {
  height: auto;
}

.tall-file-input {
  min-height: 200px !important;
}

.tall-file-input .custom-file-label {
  height: 200px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  border: 2px dashed #404656;
  border-radius: 8px;
}

.tall-file-input .custom-file-label::after {
  height: 100%;
  display: flex;
  align-items: center;
  content: "Browse";
  border-left: 1px solid #404656;
}

.tall-file-input:hover .custom-file-label {
  border-color: #13293e;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.face-card {
  background: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.face-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.face-image {
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.face-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
}

.registered-label {
  position: absolute;
  top: 20px;
  right: -35px;
  background: rgba(40, 199, 111, 0.9);
  color: white;
  padding: 5px 40px;
  transform: rotate(45deg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  z-index: 1;
}
</style>
